import { Card, Switch, Typography, InputNumber } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonProps } from "../../helpers/constants";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import NetwalaButton from "../Items/Button/Index";
import TailSpinLoader from "../Items/Loaders/TailSpinLoader";
import CompanySettingsUpdateConfirmationModal from "../Modals/CompanySettingsUpdateConfirmationModal";
import "./styles/companySettings.css";
interface SingleSettingContainer {
  value: boolean;
  label: string;
  isBorderBottom: boolean;
  handleSettingsChange: (key: string, value: number) => void;
  settingKey: string;
}
const SingleGlobalSettingComponent: React.FC<SingleSettingContainer> = ({
  value,
  label,
  isBorderBottom,
  handleSettingsChange,
  settingKey,
}) => {
  const onChange = (checked: boolean) => {
    console.log(`switch to ${checked}`);
    handleSettingsChange(settingKey, Number(checked));
  };
  return (
    <div
      className={`single-setting-container ${
        isBorderBottom === true ? "border-bottom-settings-card" : ""
      } `}
    >
      <Typography className="company-settings-label">{label}</Typography>
      <Switch value={value} onChange={onChange} />
    </div>
  );
};
const CompanyGlobalSettingsComponent = () => {
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [settings, setSettings] = useState<CompanySettingsApiProp | null>(null);
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("auth/getCompanyGlobalSettings");

  const [
    postSettingsUpdateApi,
    updateApiResponse,
    updateApiError,
    updateApiLoading,
    updateApiMessage,
    updateApiErrorCount,
  ] = useAxiosPost<UpdateCompanySettingsApiProp>(
    "auth/updateCompanyGlobalSettings"
  );
  const [show, setShow] = useState<boolean>(false);
  useEffect(() => {
    postApiData({});
  }, []);
  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      setSettings(data);
    }
  }, [apiResponse]);
  const handleSettingsChange = (key: string, value: number) => {
    if (settings === null) {
      return;
    }
    const updatedSettings = {
      ...settings,
      [key]: value,
    };
    // Clear expiryMessageDays when expiryMessagesEnabled is turned off
    if (key === "expiryMessagesEnabled" && value === 0) {
      updatedSettings.expiryMessageDays = 0;
    }
    setSettings(updatedSettings);
  };
  const handleNumericChange = (key: string, value: number | null) => {
    if (settings === null) {
      return;
    }
    setSettings({
      ...settings,
      [key]: value || 0,
    });
  };
  const handleClose = (doesUpdate: boolean) => {
    setShow(false);
    if (doesUpdate === true && settings !== null) {
      postSettingsUpdateApi(settings);
    }
  };
  useEffect(() => {
    if (updateApiResponse !== null) {
      const { message } = updateApiResponse;
      if (message) {
        openNotification(`success`, "Success", message);
        postApiData({});
      }
    }
  }, [updateApiResponse]);
  useEffect(() => {
    if (updateApiError !== null && updateApiErrorCount !== 0) {
      openNotification(`error`, "Operation Failed", updateApiError);
    }
  }, [updateApiError, updateApiErrorCount]);
  return (
    <>
      {contextHolder}
      {show === true && (
        <CompanySettingsUpdateConfirmationModal
          show={show}
          handleClose={handleClose}
        />
      )}
      <div className="company-settings-container">
        {apiLoading === true || settings === null ? (
          <TailSpinLoader />
        ) : (
          <>
            <Card className="company-settings-card">
              <SingleGlobalSettingComponent
                label="Bypass Panel Manager"
                value={Boolean(settings.isPanelManagerByPass)}
                isBorderBottom
                settingKey="isPanelManagerByPass"
                handleSettingsChange={handleSettingsChange}
              />
              <SingleGlobalSettingComponent
                label="Show Connections Password"
                value={Boolean(settings.usersPasswordViewToRman)}
                isBorderBottom
                settingKey="usersPasswordViewToRman"
                handleSettingsChange={handleSettingsChange}
              />
              <SingleGlobalSettingComponent
                label="Invoice Edit"
                value={Boolean(settings.isInvoiceAmountEditable)}
                isBorderBottom
                settingKey="isInvoiceAmountEditable"
                handleSettingsChange={handleSettingsChange}
              />
              <SingleGlobalSettingComponent
                label="Username in Invoice Receipt"
                value={Boolean(settings.usernameInInvoiceReceipt)}
                isBorderBottom
                settingKey="usernameInInvoiceReceipt"
                handleSettingsChange={handleSettingsChange}
              />
              <SingleGlobalSettingComponent
                label="Expiry Messages Enabled"
                value={Boolean(settings.expiryMessagesEnabled)}
                isBorderBottom
                settingKey="expiryMessagesEnabled"
                handleSettingsChange={handleSettingsChange}
              />
              {Boolean(settings.expiryMessagesEnabled) && (
                <div className="single-setting-container border-bottom-settings-card">
                  <Typography className="company-settings-label">
                    Expiry Message Days
                  </Typography>
                  <InputNumber
                    min={1}
                    max={10}
                    value={settings.expiryMessageDays}
                    onChange={(value) =>
                      handleNumericChange("expiryMessageDays", value)
                    }
                    style={{ width: "100px" }}
                  />
                </div>
              )}
              <div className="single-setting-container border-bottom-settings-card">
                <Typography className="company-settings-label">
                  Random Duration Start (minutes)
                </Typography>
                <InputNumber
                  min={1}
                  value={settings.randomDurationStart}
                  onChange={(value) =>
                    handleNumericChange("randomDurationStart", value)
                  }
                  style={{ width: "120px" }}
                />
              </div>
              <div className="single-setting-container border-bottom-settings-card">
                <Typography className="company-settings-label">
                  Random Duration End (minutes)
                </Typography>
                <InputNumber
                  min={1}
                  value={settings.randomDurationEnd}
                  onChange={(value) =>
                    handleNumericChange("randomDurationEnd", value)
                  }
                  style={{ width: "120px" }}
                />
              </div>
              <div className="single-setting-container">
                <Typography className="company-settings-label">
                  Messages Per Hour
                </Typography>
                <InputNumber
                  min={1}
                  max={20}
                  value={settings.messagesPerHour}
                  onChange={(value) =>
                    handleNumericChange("messagesPerHour", value)
                  }
                  style={{ width: "120px" }}
                />
              </div>
            </Card>
            <NetwalaButton
              backgroundColor={ButtonProps.background.STANDARD}
              height={ButtonProps.height.STANDARD}
              width={ButtonProps.width.STANDARD}
              textColor={ButtonProps.color.STANDARD}
              onClick={() => {
                setShow(true);
              }}
              text="Update Settings"
            />
          </>
        )}
      </div>
    </>
  );
};
export default CompanyGlobalSettingsComponent;
